/**
 * InReviewDetailPanel — Truck Pusher ticket review workspace (Phase 11 / Task 11.2, DEC-121).
 *
 * Full-page review panel opened from an InReview queue row. Read-everything:
 * job/site/driver/dispatcher fields, gauge + volume, driver/dispatcher notes, an
 * editable truck-pusher notes box (auto-saves on blur), a per-flag "what
 * triggered it" summary, the full audit timeline, photos, and the signature.
 *
 * The Acknowledge (per flag) and Approve / Send Back / Escalate (footer) actions
 * are disabled placeholders — Tasks 11.3–11.5 wire them. "View GPS trail" reuses
 * the Phase 8 GpsTrailModal. All timestamps render local (DEC-004).
 */

import * as React from 'react';
import { ArrowLeft, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import type { useApiOpts } from '@lodex-pro/web-shared';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { StatusBadge } from '../jobs/status-badge';
import { statusFullLabel } from '../jobs/job-status';
import { GpsTrailModal } from '../jobs/GpsTrailModal';
import { useUserTimezone, useUserProfile } from '../../lib/use-user-timezone';
import { formatCreated } from '../../lib/timestamp';
import { FLAG_META, StaleBadge } from './flag-meta';
import {
  useInReviewDetail,
  useUpdateInReviewNotes,
  useAcknowledgeFlag,
  useEditVolume,
  useRevertVolume,
  useApproveTicket,
  useSendBackTicket,
  useReturnToStuck,
  useEscalateTicket,
  type InReviewDetail,
  type InReviewAuditEvent,
  type InReviewFlagDetail,
  type InReviewTank,
  type MissingDisposalFeeWarning,
  type DisposalNotRecordedWarning,
  type ValidationRuleFinding,
} from './use-inreview';
import { RecordActualDisposal, UndoNoDisposal } from './RecordActualDisposal';
import { OperatorRequestNote } from '../jobs/OperatorRequestNote';
import { useJobDetail } from '../jobs/use-job-detail';
import { formatPausedTotal } from '../jobs/paused-time';
import { lineAmountCents } from '@lodex-pro/shared/money';
import { rateTag } from '@lodex-pro/shared/rate-source';
// Roles that may edit the reviewed volume (client UX gate — server authoritative on
// EDIT_TICKET_VOLUME). DEC-125 / §6 plus the DEC-313 combined BILLING_MANAGER roles.
import {
  CHANGE_SITE_ROLES,
  EDIT_VOLUME_ROLES,
  JOB_TYPE_PRICE_FIX_ROLES,
  MANAGE_DISPOSAL_FEE_ROLES,
  MANAGE_ROLES,
} from '../../lib/role-gates';
import { ChangeSiteModal } from '../jobs/ChangeSiteModal';
import { UnlinkedTanksPanel } from '../jobs/UnlinkedTanksPanel';
import { disposalFeeUrl } from '../../lib/billing-app-url';
import { CustomerRatesDialog } from './CustomerRatesDialog';
import { VoidLoadModal } from '../stuck-jobs/VoidLoadModal';
import { canStuckCancelRole } from '../stuck-jobs/stuck-jobs-format';
import { ApiError } from '@lodex-pro/web-shared';
import { ParkedTicketSection } from '../sync-conflicts/ParkedTicketSection';
import { DriverConfirmedWarnings, ValidationFindings } from './validation-rule-display';
import { PARKED_AUDIT_LABELS } from '../sync-conflicts/parked-labels';
import { JOB_SITE_CHANGE_EVENT_LABELS } from '@lodex-pro/shared/audit/job-site-change-events';
import { eventWhoLabel } from '../jobs/arrival-trigger';
import { formatNumber } from '@lodex-pro/shared/number-format';

type ApiOpts = ReturnType<typeof useApiOpts>;

interface Props {
  readonly apiOpts: ApiOpts;
  readonly jobId: string;
  readonly stale: boolean;
  readonly onBack: () => void;
}

// ── small presentational helpers ─────────────────────────────────────────────

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      style={{
        border: '1px solid var(--color-border-primary)',
        borderRadius: 'var(--radius-card)',
        backgroundColor: 'var(--color-bg-secondary)',
        padding: '14px 16px',
        marginBottom: '14px',
      }}
    >
      <h3
        style={{
          margin: '0 0 10px',
          fontSize: '12px',
          fontWeight: 700,
          color: 'var(--color-text-tertiary)',
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
      <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>{label}</span>
      <span
        style={{ fontSize: '14px', color: 'var(--color-text-primary)', wordBreak: 'break-word' }}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

const fieldGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '12px',
};

function bbl(n: number | null): string {
  return n === null ? '—' : formatNumber(n, { decimals: 1 });
}

function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/**
 * The rate with the unit it is charged in — "$1.25 / bbl", "$95.00 / hr".
 * This is the EFFECTIVE rate the server resolved (customer → operator →
 * job-type default), not a rate re-derived in the browser.
 */
/**
 * How each billing method prices, in ONE place, mirroring what the emitter actually
 * does (services/billing/emit-billable-events.ts):
 *
 *     job_types.billing_method │ emitted        │ quantity
 *     ─────────────────────────┼────────────────┼──────────────
 *     PER_BBL                  │ PER_BBL        │ net bbl
 *     HOURLY                   │ HOURLY         │ billable hours
 *     PER_LOAD                 │ FLAT_RATE      │ 1 load
 *     PER_UNIT                 │ none (line items price it individually)
 *
 * This used to be an `isHourly` ternary, which silently treated every non-hourly
 * method as per-barrel: a PER_LOAD ticket showed its tank volume in bbl next to a
 * flat price, and got no estimate at all. Both were wrong in the same way — the
 * screen exists so a truck pusher sees what approving will bill, and for a flat-rate
 * job that number is exactly knowable.
 */
interface MethodDisplay {
  /** Suffix for the rate, e.g. " / bbl". Empty when the rate is not per-unit. */
  readonly rateUnit: string;
  readonly quantity: number;
  readonly quantityLabel: string;
  readonly quantityFieldLabel: string;
  /** Whether rate × quantity is a real number rather than a guess. */
  readonly estimable: boolean;
}

export function describeBillingMethod(
  method: string | null,
  netBbl: number,
  hours: number,
): MethodDisplay {
  switch (method) {
    case 'HOURLY':
      return {
        rateUnit: ' / hr',
        quantity: hours,
        quantityLabel: `${formatNumber(hours, { decimals: 2 })} hr`,
        quantityFieldLabel: 'Billable Hours',
        estimable: true,
      };
    case 'PER_BBL':
      return {
        rateUnit: ' / bbl',
        quantity: netBbl,
        quantityLabel: `${formatNumber(netBbl, { decimals: 1 })} bbl`,
        quantityFieldLabel: 'Billable Volume',
        estimable: true,
      };
    // FLAT_RATE is what PER_LOAD emits; accept both so this reads correctly whether
    // it is handed the job type's method or the emitted one.
    case 'PER_LOAD':
    case 'FLAT_RATE':
      return {
        rateUnit: ' / load',
        quantity: 1,
        quantityLabel: '1 load',
        quantityFieldLabel: 'Billable Loads',
        estimable: true,
      };
    // PER_UNIT emits no primary event — its line items carry their own rates, so
    // there is no single rate × quantity to show and inventing one would mislead.
    case 'PER_UNIT':
      return {
        rateUnit: '',
        quantity: 0,
        quantityLabel: 'Priced per line item',
        quantityFieldLabel: 'Billable Items',
        estimable: false,
      };
    default:
      return {
        rateUnit: '',
        quantity: 0,
        quantityLabel: '—',
        quantityFieldLabel: 'Billable Quantity',
        estimable: false,
      };
  }
}

function rateLabel(cents: number | null, method: string | null): string {
  if (cents === null) return 'Not set';
  return `${usd(cents)}${describeBillingMethod(method, 0, 0).rateUnit}`;
}

/**
 * DEC-190/198 — what approving this ticket will actually bill, so the truck
 * pusher sees the rate BEFORE clicking Approve instead of discovering days later
 * that the ticket produced no invoice. Every value here comes from the server's
 * own billability assessment (the same code that emits), never recomputed here.
 */
const fixLinkStyle: React.CSSProperties = {
  color: 'var(--color-accent-blue)',
  fontWeight: 600,
  textDecoration: 'underline',
};

function BillingSummary({
  billing,
  forced,
  canEditRates,
  canFixJobTypePrice,
  onEditRates,
  canEditFees,
  onRecheck,
  rechecking,
}: {
  billing: InReviewDetail['billing'];
  /** DEC-321 — a forced ticket has no non-billable override and cannot approve with nothing. */
  forced: boolean;
  /** Task #33 — the same gate as editing the customer (MANAGE_ROLES); hides the fix link otherwise. */
  canEditRates: boolean;
  /** gap 1 D-20 — JOB_TYPE_PRICE_FIX_ROLES: may set a price for this job type for the customer. */
  canFixJobTypePrice: boolean;
  /** Opens the in-place rates dialog for `billing.haulerOperatorId`. */
  onEditRates: () => void;
  /** #52 — MANAGE_DISPOSAL_PRICES: may open the fee page to set the missing fee. */
  canEditFees: boolean;
  /** #52 — refetch the ticket after the fee was set in web-billing (another tab). */
  onRecheck: () => void;
  rechecking: boolean;
}): React.JSX.Element {
  const {
    billingMethod,
    resolvedRateCents,
    netBbl,
    hours,
    unbillable,
    haulerOperatorId,
    estimate,
    disposalFee = null,
    billingWarnings = [],
    haulPrice,
    priceDay,
    companyToday,
    jobType,
  } = billing;
  // The D-14 fix needs a billing day to start the price on; the general rate needs MANAGE_ROLES.
  const canFixRate = canEditRates || (canFixJobTypePrice && priceDay !== null);
  const display = describeBillingMethod(billingMethod, netBbl, hours);
  const missingFee = billingWarnings.find(
    (w): w is MissingDisposalFeeWarning => w.code === 'MISSING_DISPOSAL_FEE',
  );
  // Shown whenever Approve would emit anything. The amount is the server's sum over
  // the exact events Approve emits — the primary row AND every service charge and
  // line item, each rounded like the invoice. It used to be rate × quantity here,
  // which left the charges out: SOON-FIX000000041 read $120.00 and approved for
  // $790.00 (Phase 11 dev walk, 2026-10-12).
  const showEstimate = estimate.lineCount > 0;

  return (
    <Section title="Billing">
      <div
        style={{
          ...fieldGrid,
          marginBottom: unbillable === null && missingFee === undefined ? 0 : '12px',
        }}
      >
        <Field label="Billing Method" value={billingMethod} />
        <Field
          label="Effective Rate"
          value={
            <span
              style={{ color: resolvedRateCents === null ? 'var(--color-warning)' : undefined }}
            >
              {rateLabel(resolvedRateCents, billingMethod)}
              {/* gap 1 D-15 — where the rate came from, for every reviewer (never on the invoice). */}
              {haulPrice.source !== null && haulPrice.rateCents !== null && (
                <span
                  data-testid="haul-rate-tag"
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: 400,
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {rateTag(
                    haulPrice.rateCents,
                    jobType.billingMethod,
                    haulPrice.source,
                    companyToday,
                  )}
                </span>
              )}
            </span>
          }
        />
        <Field label={display.quantityFieldLabel} value={display.quantityLabel} />
        {showEstimate && (
          <Field
            label="Estimated Amount"
            value={
              <span data-testid="estimated-amount">
                {usd(estimate.totalCents)}
                {estimate.lineCount > 1 && (
                  <span
                    style={{
                      display: 'block',
                      fontSize: '12px',
                      color: 'var(--color-text-tertiary)',
                    }}
                  >
                    {formatNumber(estimate.lineCount)} billable lines, including charges and items
                  </span>
                )}
                {estimate.unpricedLineCount > 0 && (
                  <span
                    style={{ display: 'block', fontSize: '12px', color: 'var(--color-warning)' }}
                  >
                    {formatNumber(estimate.unpricedLineCount)} item
                    {estimate.unpricedLineCount === 1 ? ' has' : 's have'} no price yet — billed at
                    $0.00
                  </span>
                )}
              </span>
            }
          />
        )}
        {/* #52 (DEC-358) — the fee line Approve adds, already inside the estimate above. */}
        {disposalFee !== null && disposalFee.feeCentsPerBbl !== null && (
          <Field
            label="Disposal Fee"
            value={
              <span data-testid="disposal-fee-line">
                {disposalFee.feeCentsPerBbl === 0
                  ? `$0.00 at ${disposalFee.site.name} — no fee line`
                  : `${disposalFee.site.name} · ${bbl(netBbl)} bbl × ${usd(disposalFee.feeCentsPerBbl)} = ${usd(lineAmountCents(String(netBbl), disposalFee.feeCentsPerBbl))}`}
                <span
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  {disposalFee.source === 'CUSTOMER' ? 'Customer price' : 'Site default'}
                </span>
              </span>
            }
          />
        )}
      </div>

      {/* #52 (DEC-358 #5) — never a silent $0: set the fee, or approve without it with a
          reason (the footer button). The fee page is web-billing, a separate app, so the
          link opens a new tab; "Check again" refetches once it is saved. */}
      {missingFee !== undefined && (
        <div
          role="alert"
          data-testid="missing-disposal-fee-banner"
          style={{
            padding: '10px 12px',
            borderRadius: '12px',
            border: '1px solid var(--color-warning)',
            backgroundColor: 'rgba(246, 182, 94, 0.12)',
            color: 'var(--color-text-primary)',
            fontSize: '13px',
            lineHeight: 1.45,
          }}
        >
          <strong>
            No disposal fee set for {missingFee.customer ?? 'this customer'} at{' '}
            {missingFee.site.name}.
          </strong>{' '}
          This job type charges the customer a disposal fee (for {missingFee.day}). Set the fee, or
          approve without a disposal fee and give a reason.
          <div style={{ marginTop: '8px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {canEditFees ? (
              <a
                href={disposalFeeUrl(missingFee)}
                target="_blank"
                rel="noreferrer"
                data-testid="set-disposal-fee-link"
                style={fixLinkStyle}
              >
                Set fee now →
              </a>
            ) : (
              <span data-testid="ask-billing-fee">Ask billing to set this fee.</span>
            )}
            <button
              type="button"
              data-testid="recheck-disposal-fee"
              onClick={onRecheck}
              disabled={rechecking}
              style={{
                ...fixLinkStyle,
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: rechecking ? 'wait' : 'pointer',
                font: 'inherit',
              }}
            >
              {rechecking ? 'Checking…' : 'Check again'}
            </button>
          </div>
        </div>
      )}

      {unbillable?.reason === 'MISSING_RATE' && (
        <div
          role="alert"
          data-testid="missing-rate-banner"
          style={{
            padding: '10px 12px',
            borderRadius: '12px',
            border: '1px solid var(--color-warning)',
            backgroundColor: 'rgba(246, 182, 94, 0.12)',
            color: 'var(--color-text-primary)',
            fontSize: '13px',
            lineHeight: 1.45,
          }}
        >
          <strong>No rate is set for this customer.</strong> {unbillable.detail}{' '}
          {forced
            ? "Set a price for this job type for the customer (or the customer's general rate), or return it to the stuck list."
            : "Set a price for this job type for the customer (or the customer's general rate), or approve it as non-billable, which is recorded in the audit trail."}
          {/* Task #33 — fix the rate HERE: a pop-up on the ticket, which refetches it on
              save, instead of navigating away. A legacy-model job has no editable customer
              row (haulerOperatorId NULL), so it still links to the operators list rather
              than to an id that resolves to nothing. Roles that cannot edit the customer
              get no link at all. gap 1 D-20: a billing manager or admin also gets the button
              for the job-type price (no Sites & Operators link: they may not read it). */}
          {(haulerOperatorId === null ? canEditRates : canFixRate) && (
            <div style={{ marginTop: '8px' }}>
              {haulerOperatorId === null ? (
                <Link
                  to="/sites-operators?tab=operators"
                  data-testid="missing-rate-link"
                  style={fixLinkStyle}
                >
                  Open Sites &amp; Operators →
                </Link>
              ) : (
                <button
                  type="button"
                  data-testid="missing-rate-link"
                  onClick={onEditRates}
                  style={{
                    ...fixLinkStyle,
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  Set this customer&apos;s price →
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {unbillable?.reason === 'NO_SUBSTANCE' && (
        <div
          data-testid="no-substance-note"
          style={{
            padding: '10px 12px',
            borderRadius: '12px',
            border: '1px solid var(--color-border-secondary)',
            backgroundColor: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-secondary)',
            fontSize: '13px',
            lineHeight: 1.45,
          }}
        >
          {forced
            ? 'Nothing to bill on this ticket — no volume, hours or charges were captured. A ticket dispatch forced to review cannot be approved with nothing to bill. Correct the volume, or return it to the stuck list.'
            : 'Nothing to bill on this ticket — no volume, hours or charges were captured. Approving it will move it along without creating billable events.'}
        </div>
      )}
    </Section>
  );
}

function gauge(feet: number | null, inches: number | null): string {
  if (feet === null && inches === null) return '—';
  return `${feet ?? 0}' ${inches ?? 0}"`;
}

// ── flag data → plain-English "what triggered it" ────────────────────────────

/**
 * The flag-specific "what triggered it" line, or null when a flag has nothing beyond
 * its description. The card already renders `f.description` above this, so falling back
 * to the description here printed it twice for every flag without its own case —
 * ESCALATED among them (Phase 11 dev walk, 2026-10-12).
 */
function flagTrigger(f: InReviewFlagDetail, tz: string): React.ReactNode | null {
  const d = f.data;
  if (f.code === 'GPS_OUTSIDE_RANGE') {
    const events =
      (d['events'] as {
        eventType: string;
        distanceFt: number | null;
        thresholdFt: number | null;
      }[]) ?? [];
    if (events.length === 0) return 'One or more arrivals were outside the verified range.';
    return (
      <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
        {events.map((e, i) => (
          <li key={i} style={{ fontSize: '12px' }}>
            {e.eventType}: driver was {e.distanceFt ?? '?'} ft from site center (threshold:{' '}
            {e.thresholdFt ?? '?'} ft)
          </li>
        ))}
      </ul>
    );
  }
  if (f.code === 'VOLUME_ANOMALY') {
    return `Net ${bbl(d['actualBbl'] as number)} BBL exceeds the threshold of ${String(d['thresholdBbl'])} BBL.`;
  }
  if (f.code === 'DISPATCHER_COMPLETED') {
    const who = (d['completedBy'] as string | null) ?? 'the office';
    const role = d['completedByRole'] as string | null;
    const at = d['completedAt'] as string | null;
    return `Completed by ${who}${role ? ` (${role})` : ''}${at ? ` on ${formatCreated(at, tz)}` : ''}.`;
  }
  if (f.code === 'VALIDATION_RULE') {
    // OPEN-217 — none when the rules changed since (the card's description says so).
    return (
      <ValidationFindings findings={(d['findings'] as ValidationRuleFinding[] | undefined) ?? []} />
    );
  }
  if (f.code === 'DUPLICATE_TIMESTAMPS') {
    const ts = (d['timestamps'] as string[]) ?? [];
    return `${ts.length} timestamp${ts.length === 1 ? '' : 's'} shared by multiple events.`;
  }
  if (f.code === 'BILLING_FIELD_EDITED') {
    const reviewed = d['reviewedBbl'] as number | null;
    if (reviewed === null || reviewed === undefined)
      return 'No billing field edits on this ticket.';
    const original = d['originalBbl'] as number | null;
    const reason = d['reason'] as string | null;
    const by = d['editedBy'] as string | null;
    const at = d['editedAt'] as string | null;
    return (
      <span>
        {bbl(original)} BBL → <strong>{bbl(reviewed)} BBL</strong>
        {reason !== null ? <div>Reason: {reason}</div> : null}
        {by !== null ? (
          <div style={{ color: 'var(--color-text-tertiary)' }}>
            Edited by {by}
            {at !== null ? ` at ${formatCreated(at, tz)}` : ''}
          </div>
        ) : null}
      </span>
    );
  }
  return null;
}

// ── audit timeline row ───────────────────────────────────────────────────────

// Task 11.5 — friendly labels for the review audit events surfaced in the timeline.
const AUDIT_EVENT_LABELS: Record<string, string> = {
  TICKET_APPROVED: 'Approved',
  TICKET_SENT_BACK: 'Sent back to driver',
  TICKET_ESCALATED: 'Escalated',
  // DEC-321 / OPEN-082 — dispatch's force from the stuck list, and a reviewer's return to it.
  JOB_FORCED_TO_REVIEW: 'Forced to review by dispatch',
  TICKET_RETURNED_TO_STUCK: 'Returned to stuck list',
  // OPEN-144 (DEC-357) — a parked second run ticket and the decision on it.
  ...PARKED_AUDIT_LABELS,
  // #60 (DEC-365) — the pickup site changed (detail: "A → B · reason"), and tank re-picks.
  ...JOB_SITE_CHANGE_EVENT_LABELS,
};

function AuditRow({
  e,
  tz,
  highlight,
}: {
  e: InReviewAuditEvent;
  tz: string;
  highlight: boolean;
}): React.JSX.Element {
  const label = AUDIT_EVENT_LABELS[e.eventType] ?? e.eventType;
  const gps =
    e.gpsLat !== null && e.gpsLng !== null
      ? `${formatNumber(e.gpsLat, { decimals: 5 })}, ${formatNumber(e.gpsLng, { decimals: 5 })}${e.gpsAccuracyFt !== null ? ` ±${e.gpsAccuracyFt}ft` : ''}`
      : null;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '8px 10px',
        borderRadius: '12px',
        backgroundColor: highlight ? 'rgba(246, 182, 94, 0.10)' : 'transparent',
        borderLeft: highlight ? '3px solid var(--color-warning)' : '3px solid transparent',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {label}
        </span>
        <span
          style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}
        >
          {e.occurredAt !== null ? formatCreated(e.occurredAt, tz) : '—'}
        </span>
      </div>
      {e.detail !== null && (
        <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>— {e.detail}</span>
      )}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
          {/* OPEN-220a / DEC-386 — "· Automatic" after a geofence arrival. */}
          {eventWhoLabel(e)}
          {e.actorRole ? ` · ${e.actorRole}` : ''}
        </span>
        {e.online !== null && (
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              padding: '1px 6px',
              borderRadius: '8px',
              backgroundColor: e.online ? 'rgba(111, 207, 142, 0.15)' : 'rgba(125, 130, 142, 0.18)',
              color: e.online ? 'var(--color-success)' : 'var(--color-text-secondary)',
            }}
          >
            {e.online ? 'ONLINE' : 'OFFLINE'}
          </span>
        )}
        {e.verificationResult !== null && (
          <span style={{ fontSize: '11px', color: 'var(--color-warning)' }}>
            {e.verificationResult}
          </span>
        )}
        {gps !== null && (
          <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>{gps}</span>
        )}
      </div>
    </div>
  );
}

// ── flag summary card, incl. GPS acknowledgment (Task 11.3 / DEC-124) ─────────

interface GpsEvidenceEvent {
  eventType: string;
  occurredAt: string | null;
  distanceFt: number | null;
  thresholdFt: number | null;
  lat: number | null;
  lng: number | null;
  accuracyFt: number | null;
}

function StatusPill({ text, bg, fg }: { text: string; bg: string; fg: string }): React.JSX.Element {
  return (
    <span
      style={{
        fontSize: '11px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        whiteSpace: 'nowrap',
        backgroundColor: bg,
        color: fg,
      }}
    >
      {text}
    </span>
  );
}

/** What acknowledging an ack-required flag means, in the reviewer's words. */
function ackHelpText(code: InReviewFlagDetail['code']): string {
  if (code === 'GPS_OUTSIDE_RANGE') {
    return 'Acknowledge that you have reviewed the GPS discrepancy and approved this ticket to proceed.';
  }
  if (code === 'FORCED_BY_DISPATCH') {
    return "I've reviewed that dispatch forced this ticket and approve billing only what was captured.";
  }
  return 'Acknowledge that you have reviewed this flag and approved this ticket to proceed.';
}

function FlagCard({
  f,
  jobId,
  tz,
  onViewMap,
}: {
  f: InReviewFlagDetail;
  jobId: string;
  tz: string;
  onViewMap: () => void;
}): React.JSX.Element {
  const meta = FLAG_META[f.code];
  const isGps = f.code === 'GPS_OUTSIDE_RANGE';
  const ackMutation = useAcknowledgeFlag(jobId, f.code);
  const [ackNotes, setAckNotes] = React.useState('');

  const gpsEvents: GpsEvidenceEvent[] = isGps
    ? ((f.data['events'] as GpsEvidenceEvent[] | undefined) ?? [])
    : [];
  const siteName = isGps ? ((f.data['siteName'] as string | null) ?? null) : null;
  const trigger = isGps ? null : flagTrigger(f, tz);

  return (
    <div
      data-testid={`flag-card-${f.code}`}
      style={{
        border: '1px solid var(--color-border-primary)',
        borderLeft: `4px solid ${meta.fg}`,
        borderRadius: '12px',
        padding: '10px 12px',
        marginBottom: '8px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: 700, color: meta.fg }}>{meta.label}</span>
        {f.acknowledged ? (
          <StatusPill
            text="✓ Acknowledged"
            bg="rgba(111, 207, 142, 0.15)"
            fg="var(--color-success)"
          />
        ) : f.ackRequired ? (
          <StatusPill
            text="Acknowledgment required"
            bg="rgba(246, 182, 94, 0.15)"
            fg="var(--color-warning)"
          />
        ) : (
          <StatusPill
            text="Informational"
            bg="rgba(125, 130, 142, 0.15)"
            fg="var(--color-text-tertiary)"
          />
        )}
      </div>

      <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-text-secondary)' }}>
        {f.description}
      </p>

      {isGps ? (
        <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-text-primary)' }}>
          {siteName !== null && (
            <div style={{ marginBottom: '4px' }}>
              Pickup site: <strong>{siteName}</strong>
            </div>
          )}
          {gpsEvents.length === 0 ? (
            <span>One or more arrivals were outside the verified range.</span>
          ) : (
            gpsEvents.map((e, i) => (
              <div
                key={i}
                style={{
                  borderTop: i === 0 ? 'none' : '1px solid var(--color-border-primary)',
                  paddingTop: i === 0 ? 0 : '6px',
                  marginTop: i === 0 ? 0 : '6px',
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {e.eventType}
                  {e.occurredAt !== null ? ` · ${formatCreated(e.occurredAt, tz)}` : ''}
                </div>
                <div>Distance from site center: {e.distanceFt ?? '?'} ft</div>
                <div>Geofence radius (threshold): {e.thresholdFt ?? '?'} ft</div>
                <div>
                  Driver GPS: {e.lat !== null ? formatNumber(e.lat, { decimals: 5 }) : '?'},{' '}
                  {e.lng !== null ? formatNumber(e.lng, { decimals: 5 }) : '?'} (±
                  {e.accuracyFt ?? '?'} ft)
                </div>
              </div>
            ))
          )}
          <button
            type="button"
            onClick={onViewMap}
            style={{
              marginTop: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              fontWeight: 600,
              padding: '4px 10px',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-primary)',
              background: 'none',
              color: 'var(--color-accent-blue)',
              cursor: 'pointer',
            }}
          >
            <MapPin size={13} aria-hidden="true" /> View driver location on map
          </button>
        </div>
      ) : (
        trigger !== null && (
          <div style={{ marginTop: '4px', color: 'var(--color-text-primary)', fontSize: '12px' }}>
            {trigger}
          </div>
        )
      )}

      {/* Acknowledgment section — any ack-required flag (server-set f.ackRequired:
          GPS_OUTSIDE_RANGE, and FORCED_BY_DISPATCH since DEC-321). */}
      {f.ackRequired &&
        (f.acknowledged ? (
          <div
            style={{
              marginTop: '10px',
              padding: '8px 10px',
              borderRadius: '12px',
              backgroundColor: 'rgba(111, 207, 142, 0.10)',
              border: '1px solid rgba(111, 207, 142, 0.35)',
              fontSize: '12px',
              color: 'var(--color-success)',
            }}
          >
            <div style={{ fontWeight: 700 }}>
              ✓ Acknowledged by {f.acknowledgedBy ?? 'reviewer'}
              {f.acknowledgedAt !== null ? ` at ${formatCreated(f.acknowledgedAt, tz)}` : ''}
            </div>
            {f.acknowledgmentNotes !== null && f.acknowledgmentNotes !== '' && (
              <div style={{ marginTop: '4px', color: 'var(--color-text-secondary)' }}>
                Notes: {f.acknowledgmentNotes}
              </div>
            )}
          </div>
        ) : (
          <div style={{ marginTop: '10px' }}>
            <textarea
              value={ackNotes}
              onChange={(e): void => setAckNotes(e.target.value)}
              placeholder="Optional: add context for this acknowledgment…"
              rows={2}
              aria-label={`Acknowledgment notes for ${meta.label}`}
              style={{
                width: '100%',
                padding: '6px 8px',
                fontSize: '12px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-primary)',
                backgroundColor: 'var(--color-bg-primary)',
                color: 'var(--color-text-primary)',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              disabled={ackMutation.isPending}
              onClick={(): void => {
                const trimmed = ackNotes.trim();
                ackMutation.mutate(trimmed === '' ? {} : { notes: trimmed });
              }}
              style={{
                marginTop: '6px',
                padding: '7px 14px',
                fontSize: '13px',
                fontWeight: 700,
                borderRadius: 'var(--radius-pill)',
                border: 'none',
                cursor: ackMutation.isPending ? 'default' : 'pointer',
                backgroundColor: 'var(--color-warning)',
                color: 'var(--color-on-status)',
                opacity: ackMutation.isPending ? 0.6 : 1,
              }}
            >
              {ackMutation.isPending ? 'Acknowledging…' : `Acknowledge ${meta.label}`}
            </button>
            <p
              style={{
                margin: '6px 0 0',
                fontSize: '11px',
                color: 'var(--color-text-tertiary)',
              }}
            >
              {ackHelpText(f.code)}
            </p>
            {ackMutation.isError && (
              <p
                role="alert"
                style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--color-danger)' }}
              >
                Could not acknowledge. Try again.
              </p>
            )}
          </div>
        ))}
    </div>
  );
}

// ── reviewed-volume display + inline edit (Task 11.4 / DEC-125) ───────────────

const REASON_MIN = 10;

function VolumeReview({
  detail,
  jobId,
  tz,
  canEdit,
}: {
  detail: InReviewDetail;
  jobId: string;
  tz: string;
  canEdit: boolean;
}): React.JSX.Element {
  const c = detail.capture;
  const hasReview = c.reviewedNetBbl !== null;
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [reason, setReason] = React.useState('');
  const editMutation = useEditVolume(jobId);
  const revertMutation = useRevertVolume(jobId);

  const numValue = Number(value);
  const valueValid = value.trim() !== '' && Number.isFinite(numValue) && numValue > 0;
  const reasonValid = reason.trim().length >= REASON_MIN;
  const canSave = valueValid && reasonValid && !editMutation.isPending;

  const startEdit = (): void => {
    setValue((c.reviewedNetBbl ?? c.netBbl).toString());
    setReason('');
    setEditing(true);
  };
  const save = (): void => {
    if (!canSave) return;
    editMutation.mutate(
      { reviewedNetBbl: numValue, reason: reason.trim() },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div style={{ marginBottom: '10px' }}>
      {hasReview ? (
        <div>
          <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
            Original:{' '}
            <span style={{ textDecoration: 'line-through' }}>
              {formatNumber(c.netBbl, { decimals: 1 })} BBL
            </span>
          </span>
          <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>
            Reviewed BBL
          </div>
          <div style={{ fontSize: '30px', fontWeight: 800, color: 'var(--color-success)' }}>
            {formatNumber(c.reviewedNetBbl ?? 0, { decimals: 1 })}
          </div>
          {c.reviewedNetBblReason !== null && (
            <div
              style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '4px' }}
            >
              Reason: {c.reviewedNetBblReason}
            </div>
          )}
          {c.reviewedBy !== null && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
              Edited by {c.reviewedBy}
              {c.reviewedAt !== null ? ` at ${formatCreated(c.reviewedAt, tz)}` : ''}
            </div>
          )}
        </div>
      ) : (
        <div>
          <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>Net BBL</span>
          <div style={{ fontSize: '30px', fontWeight: 800, color: 'var(--color-text-primary)' }}>
            {formatNumber(c.netBbl, { decimals: 1 })}
          </div>
        </div>
      )}

      {/* Actions */}
      {canEdit && !editing && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button
            type="button"
            onClick={startEdit}
            data-testid="edit-volume-button"
            style={{
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--color-border-primary)',
              background: 'none',
              color: 'var(--color-accent-blue)',
              cursor: 'pointer',
            }}
          >
            Edit Volume
          </button>
          {hasReview && (
            <button
              type="button"
              disabled={revertMutation.isPending}
              onClick={(): void => revertMutation.mutate()}
              data-testid="revert-volume-button"
              style={{
                padding: '6px 12px',
                fontSize: '13px',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--color-border-primary)',
                background: 'none',
                color: 'var(--color-text-tertiary)',
                cursor: revertMutation.isPending ? 'default' : 'pointer',
              }}
            >
              {revertMutation.isPending ? 'Reverting…' : 'Revert'}
            </button>
          )}
        </div>
      )}
      {revertMutation.isError && (
        <p
          role="alert"
          style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-danger)' }}
        >
          Could not revert. Only the original editor or a tenant admin can revert an edit.
        </p>
      )}

      {/* Inline edit form */}
      {canEdit && editing && (
        <div
          style={{
            marginTop: '10px',
            padding: '10px',
            borderRadius: '12px',
            border: '1px solid var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-primary)',
          }}
        >
          <div
            style={{ fontSize: '12px', color: 'var(--color-text-tertiary)', marginBottom: '6px' }}
          >
            Current net: <strong>{formatNumber(c.netBbl, { decimals: 1 })} BBL</strong>
          </div>
          <label style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
            New volume (BBL)
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={value}
            onChange={(e): void => setValue(e.target.value)}
            aria-label="New reviewed volume in BBL"
            style={{
              display: 'block',
              width: '100%',
              padding: '6px 8px',
              margin: '2px 0 8px',
              fontSize: '14px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-primary)',
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-primary)',
              boxSizing: 'border-box',
            }}
          />
          <label style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
            Reason (required, min {REASON_MIN} chars)
          </label>
          <textarea
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            placeholder="Explain why you are changing this volume (e.g. gauge reading error, misread tank…)"
            rows={3}
            aria-label="Reason for volume edit"
            style={{
              display: 'block',
              width: '100%',
              padding: '6px 8px',
              margin: '2px 0 2px',
              fontSize: '13px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-primary)',
              backgroundColor: 'var(--color-bg-secondary)',
              color: 'var(--color-text-primary)',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <div
            style={{
              fontSize: '11px',
              color: reasonValid ? 'var(--color-text-tertiary)' : 'var(--color-warning)',
            }}
          >
            {reason.trim().length}/{REASON_MIN} characters
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={!canSave}
              onClick={save}
              data-testid="save-volume-button"
              style={{
                padding: '7px 14px',
                fontSize: '13px',
                fontWeight: 700,
                borderRadius: 'var(--radius-pill)',
                border: 'none',
                cursor: canSave ? 'pointer' : 'not-allowed',
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
                opacity: canSave ? 1 : 0.5,
              }}
            >
              {editMutation.isPending ? 'Saving…' : 'Save Edit'}
            </button>
            <button
              type="button"
              onClick={(): void => setEditing(false)}
              style={{
                padding: '7px 14px',
                fontSize: '13px',
                fontWeight: 600,
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--color-border-primary)',
                background: 'none',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
          {value.trim() !== '' && !valueValid && (
            <p
              role="alert"
              style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-danger)' }}
            >
              Enter a volume greater than 0.
            </p>
          )}
          {editMutation.isError && (
            <p
              role="alert"
              style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-danger)' }}
            >
              Could not save the edit. The new volume must differ from the recorded net.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** OPEN-169 addendum — shown where Send Back would be when the company has it off. */
export const SEND_BACK_OFF_HINT =
  'Fix it here: record the actual disposal, correct the volume, or record that no disposal happened. Billing can correct the billed lines.';

// ── reason-required action modal (Send Back / Escalate — Task 11.5) ───────────

const ACTION_REASON_MIN = 10;

function ReasonActionModal({
  open,
  onClose,
  title,
  warning,
  placeholder,
  confirmLabel,
  confirmColor,
  pending,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  warning: string;
  placeholder: string;
  confirmLabel: string;
  confirmColor: string;
  pending: boolean;
  onConfirm: (reason: string) => void;
}): React.JSX.Element {
  const [reason, setReason] = React.useState('');
  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const valid = reason.trim().length >= ACTION_REASON_MIN;

  return (
    <Dialog
      open={open}
      onOpenChange={(next): void => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              borderRadius: '12px',
              border: '1px solid var(--color-border-secondary)',
              backgroundColor: 'var(--color-bg-tertiary)',
              color: 'var(--color-text-secondary)',
              fontSize: '13px',
              lineHeight: 1.4,
            }}
          >
            {warning}
          </div>
          <textarea
            value={reason}
            onChange={(e): void => setReason(e.target.value)}
            placeholder={placeholder}
            aria-label={`${confirmLabel} reason`}
            style={{
              width: '100%',
              minHeight: '90px',
              padding: '8px 10px',
              fontSize: '14px',
              backgroundColor: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border-secondary)',
              borderRadius: '10px',
              color: 'var(--color-text-primary)',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
          <span
            style={{
              fontSize: '12px',
              color: valid ? 'var(--color-text-tertiary)' : 'var(--color-warning)',
            }}
          >
            {reason.trim().length}/{ACTION_REASON_MIN} characters
          </span>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!valid || pending}
            onClick={(): void => onConfirm(reason.trim())}
            style={{
              backgroundColor: confirmColor,
              color: 'var(--color-on-status)',
              borderColor: confirmColor,
              opacity: !valid || pending ? 0.6 : 1,
            }}
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── forced-review banner (DEC-321 / OPEN-082) ─────────────────────────────────

/**
 * Shown above everything else when dispatch forced this stuck job into review: who, when
 * (local, DEC-004), why, and the step it was stuck at. The ticket was never finished, so the
 * reviewer must read this before billing anything on it.
 */
function ForcedReviewBanner({
  forced,
  tz,
}: {
  forced: NonNullable<InReviewDetail['forcedReview']>;
  tz: string;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      data-testid="forced-review-banner"
      style={{
        padding: '12px 14px',
        marginBottom: '16px',
        borderRadius: '12px',
        border: '1px solid var(--color-danger)',
        backgroundColor: 'rgba(243, 139, 139, 0.12)',
        color: 'var(--color-text-primary)',
        fontSize: '13px',
        lineHeight: 1.45,
      }}
    >
      <div
        style={{
          fontSize: '15px',
          fontWeight: 700,
          color: 'var(--color-danger)',
          marginBottom: '4px',
        }}
      >
        Forced to review by dispatch
      </div>
      <div>
        {forced.byName ?? 'A dispatcher'} · {formatCreated(forced.at, tz)}
      </div>
      {forced.reason !== null && <div>Reason: {forced.reason}</div>}
      {/* DEC-329 */}
      {forced.approvedByName !== undefined && forced.approvedByName !== null && (
        <div data-testid="forced-review-approved-by">Approved by: {forced.approvedByName}</div>
      )}
      {forced.priorStatus !== null && <div>Stuck at: {statusFullLabel(forced.priorStatus)}</div>}
      <div style={{ marginTop: '6px', fontWeight: 600 }}>
        The driver never finished this ticket and the load never reached disposal.
      </div>
    </div>
  );
}

// ── main panel ───────────────────────────────────────────────────────────────

export function InReviewDetailPanel({ apiOpts, jobId, stale, onBack }: Props): React.JSX.Element {
  const tz = useUserTimezone();
  const { data: profileData } = useUserProfile();
  const canEditVolume = EDIT_VOLUME_ROLES.has(profileData?.profile.role ?? '');
  // Task #33 — the customer screen's own edit gate (SitesOperatorsPage canManage).
  const canEditRates = MANAGE_ROLES.has(profileData?.profile.role ?? '');
  // gap 1 D-20 — the In Review "price for this job type" fix (MANAGE_CUSTOMER_PRICES + REVIEW_TICKET).
  const canFixJobTypePrice = JOB_TYPE_PRICE_FIX_ROLES.has(profileData?.profile.role ?? '');
  // #52 — the fee page's own save gate (MANAGE_DISPOSAL_PRICES).
  const canEditFees = MANAGE_DISPOSAL_FEE_ROLES.has(profileData?.profile.role ?? '');
  const [ratesOpen, setRatesOpen] = React.useState(false);
  // DEC-355 (OPEN-151 PR 2) — "Void load and cancel" for a ticket whose load should not exist,
  // forced or driver-finished. Manager tier only; nothing is billed until approval, so voiding
  // from review cannot strand money.
  const canVoidLoad = canStuckCancelRole(profileData?.profile.role ?? '');
  const [voidOpen, setVoidOpen] = React.useState(false);
  // #60 (DEC-365) — a reviewer may correct the pickup site up to approval (CHANGE_JOB_SITE),
  // and pick the tank for any run-ticket tank the change left unlinked.
  const canChangeSite = CHANGE_SITE_ROLES.has(profileData?.profile.role ?? '');
  const [changeSiteOpen, setChangeSiteOpen] = React.useState(false);
  const { data, isLoading, isError, refetch, isFetching } = useInReviewDetail(jobId);
  // OPEN-153 / DEC-354 — total paused time comes from GET /v1/jobs/:id (the review
  // endpoint does not carry it); shown under Completed in Job Details.
  const { data: jobDetailData } = useJobDetail(apiOpts, jobId);
  const pausedLabel = formatPausedTotal(
    jobDetailData?.job.pausedSeconds,
    jobDetailData?.job.pauseCount,
  );
  const notesMutation = useUpdateInReviewNotes(jobId);
  const approveMutation = useApproveTicket(jobId);
  const sendBackMutation = useSendBackTicket(jobId);
  const returnToStuckMutation = useReturnToStuck(jobId);
  const escalateMutation = useEscalateTicket(jobId);

  const [notes, setNotes] = React.useState('');
  const [notesDirty, setNotesDirty] = React.useState(false);
  const [showTrail, setShowTrail] = React.useState(false);
  const [lightbox, setLightbox] = React.useState<string | null>(null);
  const [sendBackOpen, setSendBackOpen] = React.useState(false);
  const [returnOpen, setReturnOpen] = React.useState(false);
  const [escalateOpen, setEscalateOpen] = React.useState(false);
  const [overrideOpen, setOverrideOpen] = React.useState(false);
  const [skipFeeOpen, setSkipFeeOpen] = React.useState(false);

  const missingRate = data?.billing.unbillable?.reason === 'MISSING_RATE';
  // #52 (DEC-358 #5) — the API refuses approve until the fee is set or skipped with a reason.
  const missingFee =
    data?.billing.billingWarnings?.some((w) => w.code === 'MISSING_DISPOSAL_FEE') === true;
  // Bug 2 (Corbin) — no actual disposal on a disposal-ending load: the API refuses approve
  // until it is recorded (no "approve without"), and the panel offers to record it.
  const notRecorded = data?.billing.billingWarnings?.find(
    (w): w is DisposalNotRecordedWarning => w.code === 'DISPOSAL_NOT_RECORDED',
  );
  // DEC-321 / OPEN-082 — dispatch forced this ticket to review. The API refuses Send Back,
  // the non-billable override, and any approve with nothing billable on it.
  const forced = data !== undefined && data.forcedReview !== null;
  const forcedUnbillable = forced && data.billing.unbillable !== null;
  // OPEN-169 addendum — Send Back is a per-company setting, default OFF (the API refuses it
  // too). An API that does not send the field is treated as off.
  const allowSendBack = data?.allowSendBack === true;
  // OPEN-144 (DEC-357) — a parked second ticket blocks Approve until Keep or Use (the API
  // refuses too, with PARKED_CAPTURE_UNRESOLVED; this says so before the click).
  const parked = data?.inreviewFlags.includes('PARKED_CAPTURE') ?? false;

  // The body is always sent, even when empty — see useApproveTicket. `undefined`
  // here produced a bodiless PATCH that the API answered with a 500 (OPEN-040).
  const onApprove = React.useCallback(
    (approveNonBillable = false): void => {
      approveMutation.mutate(approveNonBillable ? { approveNonBillable: true } : {}, {
        onSuccess: (res) => {
          setOverrideOpen(false);
          toast.success(
            res.unbillable === null
              ? 'Ticket approved — moved to billing'
              : 'Ticket approved as non-billable — no invoice will be generated',
          );
          onBack();
        },
        // useApiMutation keeps the status and code, not the API's reason sentence, so the
        // message is chosen from the code and what the panel already knows about the ticket.
        onError: (err) =>
          toast.error(
            err instanceof ApiError && err.code === 'PARKED_CAPTURE_UNRESOLVED'
              ? 'Could not approve — a second ticket is parked on this job. Keep the saved ticket or use the parked one first.'
              : forcedUnbillable
                ? 'Could not approve — this ticket was forced to review and has nothing that can be billed. Correct the volume or set a rate, or return it to the stuck list.'
                : missingRate
                  ? 'Could not approve — this ticket has no rate. Set one for this customer, or approve it as non-billable.'
                  : missingFee
                    ? 'Could not approve — no disposal fee is set. Set the fee, or approve without a disposal fee.'
                    : 'Could not approve — acknowledge all required flags first.',
          ),
      });
    },
    [approveMutation, onBack, missingRate, missingFee, forcedUnbillable],
  );

  // #52 — approve without the missing disposal fee; the reason goes on the audit row.
  const onSkipFee = React.useCallback(
    (reason: string): void => {
      approveMutation.mutate(
        { skipDisposalFee: { reason } },
        {
          onSuccess: () => {
            setSkipFeeOpen(false);
            toast.success('Ticket approved without a disposal fee — moved to billing');
            onBack();
          },
          // A fee set meanwhile still approves (the skip is then ignored); any other refusal
          // is the ack gate, or the ticket changed under us.
          onError: () =>
            toast.error('Could not approve — acknowledge all required flags first, then retry.'),
        },
      );
    },
    [approveMutation, onBack],
  );

  const onReturnToStuck = React.useCallback(
    (reason: string): void => {
      returnToStuckMutation.mutate(
        { reason },
        {
          onSuccess: () => {
            setReturnOpen(false);
            toast.success('Returned to the stuck list');
            onBack();
          },
          onError: () => toast.error('Could not return the ticket to the stuck list. Try again.'),
        },
      );
    },
    [returnToStuckMutation, onBack],
  );

  const onSendBack = React.useCallback(
    (reason: string): void => {
      sendBackMutation.mutate(
        { reason },
        {
          onSuccess: () => {
            setSendBackOpen(false);
            toast.success('Ticket sent back to driver');
            onBack();
          },
          onError: () => toast.error('Could not send the ticket back. Try again.'),
        },
      );
    },
    [sendBackMutation, onBack],
  );

  const onEscalate = React.useCallback(
    (reason: string): void => {
      escalateMutation.mutate(
        { reason },
        {
          onSuccess: () => {
            setEscalateOpen(false);
            toast.success('Ticket escalated to management');
            // Stays in the InReview queue with an ESCALATED badge — do NOT navigate.
          },
          onError: () => toast.error('Could not escalate the ticket. Try again.'),
        },
      );
    },
    [escalateMutation],
  );

  // Seed the notes box once the detail loads (and when it changes on refetch,
  // unless the user has unsaved edits).
  React.useEffect(() => {
    if (data !== undefined && !notesDirty) setNotes(data.truckPusherNotes ?? '');
  }, [data, notesDirty]);

  const saveNotes = React.useCallback((): void => {
    if (!notesDirty) return;
    notesMutation.mutate(
      { notes },
      {
        onSuccess: () => {
          setNotesDirty(false);
        },
      },
    );
  }, [notes, notesDirty, notesMutation]);

  if (isLoading || data === undefined) {
    return <PanelShell onBack={onBack}>Loading ticket…</PanelShell>;
  }
  if (isError) {
    return <PanelShell onBack={onBack}>Failed to load this ticket.</PanelShell>;
  }
  const t: InReviewDetail = data;

  // Highlight OUTSIDE_RANGE + duplicate-timestamp events in the timeline.
  const dupTimestamps = new Set(
    (t.flagDetails.find((f) => f.code === 'DUPLICATE_TIMESTAMPS')?.data['timestamps'] as
      | string[]
      | undefined) ?? [],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-secondary)',
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            border: 'none',
            background: 'none',
            color: 'var(--color-accent-blue)',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          <ArrowLeft size={15} aria-hidden="true" /> Back to queue
        </button>
        <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--color-text-primary)' }}>
          {t.haulId ?? '—'}
        </span>
        <StatusBadge statusCode={t.status} full />
        {stale && <StaleBadge />}
        <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }}>
          {t.inreviewFlags.map((code) => (
            <span
              key={code}
              style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 'var(--radius-pill)',
                backgroundColor: FLAG_META[code].bg,
                color: FLAG_META[code].fg,
              }}
            >
              {FLAG_META[code].label}
            </span>
          ))}
        </div>
        <button
          type="button"
          onClick={(): void => {
            setShowTrail(true);
          }}
          style={{
            marginLeft: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: 'var(--radius-pill)',
            border: '1px solid var(--color-border-primary)',
            backgroundColor: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
            cursor: 'pointer',
          }}
        >
          <MapPin size={15} aria-hidden="true" /> View GPS trail
        </button>
      </div>

      {/* Body — two columns */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '16px' }}>
        {t.forcedReview !== null && <ForcedReviewBanner forced={t.forcedReview} tz={tz} />}
        {notRecorded !== undefined && (
          <RecordActualDisposal
            jobId={jobId}
            intended={notRecorded.intended}
            detail={notRecorded.detail}
          />
        )}
        {t.destination.noDisposal != null && (
          <UndoNoDisposal jobId={jobId} reason={t.destination.noDisposal.reason} />
        )}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '16px',
            alignItems: 'start',
          }}
        >
          {/* LEFT COLUMN */}
          <div>
            <Section title="Job Details">
              <div style={fieldGrid}>
                <Field label="Job Type" value={t.jobType} />
                <Field label="Site" value={t.site.siteName} />
                {t.site.sourceWellName != null && (
                  <Field label="Source Well" value={t.site.sourceWellName} />
                )}
                <Field label="Operator" value={t.site.operatorName} />
                <Field label="API Number" value={t.site.apiNumber} />
                <Field label="State" value={t.site.state} />
                <Field label="Destination" value={t.destination.name} />
                {/* Bug 2 (Corbin) — the intended and the actual disposal, side by side. */}
                <Field label="Intended Disposal" value={t.destination.intended ?? null} />
                <Field
                  label="Actual Disposal"
                  value={
                    t.destination.noDisposal != null
                      ? 'None (no disposal happened)'
                      : (t.destination.actual ?? null)
                  }
                />
                {/* 0216 — why the office recorded no disposal, and who. */}
                {t.destination.noDisposal != null && (
                  <Field
                    label="No Disposal Reason"
                    value={
                      t.destination.noDisposal.byName != null
                        ? `${t.destination.noDisposal.reason ?? ''} (${t.destination.noDisposal.byName})`
                        : t.destination.noDisposal.reason
                    }
                  />
                )}
                <Field label="Driver" value={t.driver.name} />
                <Field label="Driver Phone" value={t.driver.phone} />
                <Field label="Dispatcher" value={t.dispatcher.name} />
                {/* Billing Method moved to the Billing section, next to the rate it applies. */}
                <Field label="Created" value={fmt(t.timing.createdAt, tz)} />
                <Field label="Completed" value={fmt(t.timing.completedAt, tz)} />
                <Field label="Paused" value={pausedLabel} />
                <Field label="Entered Review" value={fmt(t.timing.inreviewAt, tz)} />
              </div>
            </Section>

            <BillingSummary
              billing={t.billing}
              forced={t.forcedReview !== null}
              canEditRates={canEditRates}
              canFixJobTypePrice={canFixJobTypePrice}
              onEditRates={(): void => setRatesOpen(true)}
              canEditFees={canEditFees}
              onRecheck={(): void => void refetch()}
              rechecking={isFetching}
            />
            {/* Mounted only while open, so the customer is fetched only when asked for. */}
            {ratesOpen &&
              t.billing.haulerOperatorId !== null &&
              (canEditRates || (canFixJobTypePrice && t.billing.priceDay !== null)) && (
                <CustomerRatesDialog
                  open={ratesOpen}
                  onOpenChange={setRatesOpen}
                  haulerOperatorId={t.billing.haulerOperatorId}
                  customerName={t.site.operatorName}
                  jobId={jobId}
                  jobType={t.billing.jobType}
                  priceDay={t.billing.priceDay}
                  allowJobType={canFixJobTypePrice && t.billing.priceDay !== null}
                  allowGeneral={canEditRates}
                />
              )}

            {/* OPEN-144 PR 3 — decide a parked second ticket before approving. */}
            {parked && <ParkedTicketSection jobId={jobId} />}
            <Section title="Gauge & Volume">
              <VolumeReview detail={t} jobId={jobId} tz={tz} canEdit={canEditVolume} />
              <div style={{ ...fieldGrid, marginBottom: '10px' }}>
                <Field label="Captured At" value={fmt(t.capture.capturedAt, tz)} />
                <Field label="Producer" value={t.capture.producerIdentifier} />
              </div>
              {canChangeSite && (
                <UnlinkedTanksPanel
                  jobId={jobId}
                  unlinkedTankNames={
                    jobDetailData?.job.siteChangeTankOutcome?.unlinkedTankNames ?? []
                  }
                />
              )}
              {t.capture.tanks.length === 0 ? (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  No tank readings recorded.
                </span>
              ) : (
                t.capture.tanks.map((tank: InReviewTank, i) => (
                  <div
                    key={i}
                    style={{
                      border: '1px solid var(--color-border-primary)',
                      borderRadius: '12px',
                      padding: '10px',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                      {tank.tankName}
                    </div>
                    <div style={fieldGrid}>
                      <Field label="Open (before)" value={bbl(tank.beforeBbl)} />
                      <Field label="Close (after)" value={bbl(tank.afterBbl)} />
                      <Field
                        label="Top Gauge"
                        value={gauge(tank.topGaugeFeet, tank.topGaugeInches)}
                      />
                      <Field
                        label="Bottom Gauge"
                        value={gauge(tank.bottomGaugeFeet, tank.bottomGaugeInches)}
                      />
                      <Field label="Water BBL" value={bbl(tank.waterVolumeBbl)} />
                      <Field label="Oil BBL" value={bbl(tank.oilVolumeBbl)} />
                      {tank.colorCut !== null && <Field label="Color Cut" value={tank.colorCut} />}
                    </div>
                  </div>
                ))
              )}
            </Section>

            <Section title="Driver Notes">
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                {t.capture.driverNotes ?? 'No driver notes.'}
              </p>
            </Section>

            {/* DEC-299 / Task 18.6 — the operator's note on an API haul request, in its own
                labelled block and never folded into Dispatcher Notes. Renders nothing for a
                job no operator requested through the API. */}
            <OperatorRequestNote notes={t.operatorRequestNotes} testId="inreview-operator-note" />

            <Section title="Dispatcher Notes">
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-text-secondary)' }}>
                {t.dispatcherNotes ?? 'No dispatcher notes.'}
              </p>
            </Section>

            <Section title="Truck Pusher Notes">
              <textarea
                value={notes}
                onChange={(e): void => {
                  setNotes(e.target.value);
                  setNotesDirty(true);
                }}
                onBlur={saveNotes}
                placeholder="Add a review note…"
                rows={4}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '14px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border-primary)',
                  backgroundColor: 'var(--color-bg-primary)',
                  color: 'var(--color-text-primary)',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  onClick={saveNotes}
                  disabled={!notesDirty || notesMutation.isPending}
                  style={{
                    padding: '7px 14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                    cursor: notesDirty && !notesMutation.isPending ? 'pointer' : 'default',
                    backgroundColor: 'var(--color-accent-blue)',
                    color: 'var(--color-on-accent)',
                    opacity: !notesDirty || notesMutation.isPending ? 0.6 : 1,
                  }}
                >
                  {notesMutation.isPending ? 'Saving…' : 'Save Notes'}
                </button>
                {notesMutation.isSuccess && !notesDirty && (
                  <span style={{ fontSize: '12px', color: 'var(--color-success)' }}>Saved.</span>
                )}
              </div>
            </Section>
          </div>

          {/* RIGHT COLUMN */}
          <div>
            <Section title="Flag Summary">
              {t.flagDetails.length === 0 ? (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  No flags on this ticket.
                </span>
              ) : (
                t.flagDetails.map((f) => (
                  <FlagCard
                    key={f.code}
                    f={f}
                    jobId={jobId}
                    tz={tz}
                    onViewMap={(): void => setShowTrail(true)}
                  />
                ))
              )}
            </Section>

            {/* OPEN-217 (DEC-382) — Warnings the driver saw and confirmed on the phone. */}
            {(t.driverConfirmedWarnings?.length ?? 0) > 0 && (
              <Section title="Driver confirmed warnings">
                <DriverConfirmedWarnings warnings={t.driverConfirmedWarnings ?? []} tz={tz} />
              </Section>
            )}

            <Section title="Audit Timeline">
              {t.auditTimeline.length === 0 ? (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  No events recorded.
                </span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {t.auditTimeline.map((e, i) => (
                    <AuditRow
                      key={i}
                      e={e}
                      tz={tz}
                      highlight={
                        e.verificationResult === 'OUTSIDE_RANGE' ||
                        (e.occurredAt !== null && dupTimestamps.has(e.occurredAt))
                      }
                    />
                  ))}
                </div>
              )}
            </Section>

            <Section title="Photos">
              {t.capture.photos.length === 0 ? (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  No photos
                </span>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {t.capture.photos.map((p, i) =>
                    p.url === null ? (
                      <div
                        key={i}
                        style={{
                          width: '96px',
                          height: '96px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          textAlign: 'center',
                          color: 'var(--color-text-tertiary)',
                          border: '1px dashed var(--color-border-primary)',
                          borderRadius: '10px',
                        }}
                      >
                        Unavailable
                      </div>
                    ) : (
                      <img
                        key={i}
                        src={p.url}
                        alt={`Ticket photo ${i + 1}`}
                        onClick={(): void => {
                          setLightbox(p.url);
                        }}
                        style={{
                          width: '96px',
                          height: '96px',
                          objectFit: 'cover',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          border: '1px solid var(--color-border-primary)',
                        }}
                      />
                    ),
                  )}
                </div>
              )}
            </Section>

            <Section title="Signature">
              {!t.capture.signature.present ? (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  No signature captured
                </span>
              ) : t.capture.signature.url === null ? (
                <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>
                  Signature captured (image unavailable)
                </span>
              ) : (
                <img
                  src={t.capture.signature.url}
                  alt="Driver signature"
                  onClick={(): void => {
                    setLightbox(t.capture.signature.url);
                  }}
                  style={{
                    maxWidth: '260px',
                    maxHeight: '120px',
                    backgroundColor: 'var(--color-paper)',
                    borderRadius: '10px',
                    padding: '6px',
                    cursor: 'pointer',
                  }}
                />
              )}
            </Section>
          </div>
        </div>
      </div>

      {/* Footer action bar — placeholders (Task 11.5 wires these) */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          padding: '12px 16px',
          borderTop: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-secondary)',
          flexShrink: 0,
        }}
      >
        {/* Task 11.3/11.5 — Approve is gated on all ack-required flags being
            acknowledged (server-computed t.canApprove) + advances to billing. */}
        {/* DEC-190 — a ticket with real work but no rate must not be approved
            silently into a void. The plain Approve is blocked (the server blocks
            it too; this just says so before the click), and the override is a
            separate, deliberate, audited action. */}
        {(() => {
          // DEC-321 — the API refuses a forced ticket with nothing billable; say so up front.
          const blocked =
            !t.canApprove ||
            missingRate ||
            forcedUnbillable ||
            parked ||
            missingFee ||
            notRecorded !== undefined;
          const enabled = !blocked && !approveMutation.isPending;
          const unacked = t.flagDetails
            .filter((f) => f.ackRequired && !f.acknowledged)
            .map((f) => FLAG_META[f.code].label);
          return (
            <button
              type="button"
              disabled={blocked || approveMutation.isPending}
              onClick={(): void => onApprove(false)}
              data-testid="approve-button"
              title={
                notRecorded !== undefined
                  ? 'Record where the water actually went before approving'
                  : parked
                    ? 'A second ticket is parked on this job — keep the saved ticket or use the parked one first'
                    : forcedUnbillable
                      ? 'This ticket was forced to review and has nothing that can be billed — correct the volume or set a rate, or return it to the stuck list'
                      : missingRate
                        ? 'This ticket has no rate — set one for this customer, or approve it as non-billable'
                        : missingFee
                          ? 'No disposal fee is set — set the fee, or approve without a disposal fee'
                          : t.canApprove
                            ? undefined
                            : unacked.length > 0
                              ? `Acknowledge the ${unacked.join(' and ')} flag${unacked.length === 1 ? '' : 's'} before approving this ticket`
                              : 'Acknowledge the required flags before approving this ticket'
              }
              style={{
                padding: '9px 16px',
                fontSize: '14px',
                fontWeight: 600,
                borderRadius: '14px',
                border: 'none',
                backgroundColor: 'var(--color-success)',
                color: 'var(--color-on-status)',
                opacity: enabled ? 1 : 0.45,
                cursor: enabled ? 'pointer' : 'not-allowed',
              }}
            >
              {approveMutation.isPending ? 'Approving…' : 'Approve'}
            </button>
          );
        })()}
        {/* DEC-321 — never on a forced ticket: the API refuses the override there. */}
        {missingRate && !forced && (
          <button
            type="button"
            disabled={!t.canApprove || approveMutation.isPending}
            onClick={(): void => setOverrideOpen(true)}
            data-testid="approve-non-billable-button"
            title={
              t.canApprove
                ? 'Approve without billing — recorded in the audit trail'
                : 'Acknowledge the required flags first'
            }
            style={{
              ...footerActionStyle('var(--color-warning)'),
              opacity: t.canApprove && !approveMutation.isPending ? 1 : 0.45,
              cursor: t.canApprove && !approveMutation.isPending ? 'pointer' : 'not-allowed',
            }}
          >
            Approve as Non-Billable
          </button>
        )}
        {/* #52 (DEC-358 #5) — the haul still bills; only the fee is left off, with a reason.
            Allowed on a forced ticket too: it is not a non-billable approve. */}
        {missingFee && (
          <button
            type="button"
            disabled={!t.canApprove || approveMutation.isPending}
            onClick={(): void => setSkipFeeOpen(true)}
            data-testid="approve-without-fee-button"
            title={
              t.canApprove
                ? 'Approve without the disposal fee — your reason is recorded in the audit trail'
                : 'Acknowledge the required flags first'
            }
            style={{
              ...footerActionStyle('var(--color-warning)'),
              opacity: t.canApprove && !approveMutation.isPending ? 1 : 0.45,
              cursor: t.canApprove && !approveMutation.isPending ? 'pointer' : 'not-allowed',
            }}
          >
            Approve without disposal fee
          </button>
        )}
        {/* DEC-321 — a forced ticket was never finished, so there is nothing to send back to
            the driver; it goes back to the stuck list instead (the API refuses Send Back). */}
        {forced ? (
          <button
            type="button"
            onClick={(): void => setReturnOpen(true)}
            data-testid="return-to-stuck-button"
            style={footerActionStyle('var(--color-warning)')}
          >
            Return to stuck list
          </button>
        ) : allowSendBack ? (
          <button
            type="button"
            onClick={(): void => setSendBackOpen(true)}
            data-testid="send-back-button"
            style={footerActionStyle('var(--color-warning)')}
          >
            Send Back to Driver
          </button>
        ) : (
          // OPEN-169 addendum — Send Back is off for this company (the phone cannot correct a
          // sent-back ticket yet), so the fixes happen here in review.
          <span
            data-testid="send-back-off-hint"
            style={{
              alignSelf: 'center',
              fontSize: 13,
              color: 'var(--color-text-secondary)',
              maxWidth: 360,
            }}
          >
            {SEND_BACK_OFF_HINT}
          </span>
        )}
        <button
          type="button"
          onClick={(): void => setEscalateOpen(true)}
          data-testid="escalate-button"
          style={footerActionStyle('var(--color-danger)')}
        >
          Escalate
        </button>
        {canChangeSite ? (
          <button
            type="button"
            onClick={(): void => setChangeSiteOpen(true)}
            data-testid="change-site-button"
            style={footerActionStyle('var(--color-accent-blue)')}
          >
            Change site
          </button>
        ) : null}
        {canVoidLoad ? (
          <button
            type="button"
            onClick={(): void => setVoidOpen(true)}
            data-testid="void-load-button"
            style={footerActionStyle('var(--color-danger)')}
          >
            Void load and cancel
          </button>
        ) : null}
      </div>

      <ReasonActionModal
        open={sendBackOpen}
        onClose={(): void => setSendBackOpen(false)}
        title="Send ticket back to driver"
        warning="The driver will need to resubmit this ticket. It will return to their active jobs."
        placeholder="Explain what needs to be corrected…"
        confirmLabel="Send Back"
        confirmColor="var(--color-warning)"
        pending={sendBackMutation.isPending}
        onConfirm={onSendBack}
      />
      <ReasonActionModal
        open={returnOpen}
        onClose={(): void => setReturnOpen(false)}
        title="Return to stuck list"
        warning="The job goes back to the step it was stuck at and reappears on the stuck list. The driver's clock stays stopped."
        placeholder="Explain why this ticket is going back to the stuck list…"
        confirmLabel="Return to stuck list"
        confirmColor="var(--color-warning)"
        pending={returnToStuckMutation.isPending}
        onConfirm={onReturnToStuck}
      />
      <ReasonActionModal
        open={skipFeeOpen}
        onClose={(): void => setSkipFeeOpen(false)}
        title="Approve without disposal fee"
        warning="The haul bills as normal but no disposal fee is charged on this ticket. Billing can add it later from the ticket editor. Your reason is recorded in the audit trail."
        placeholder="Why does this ticket carry no disposal fee?"
        confirmLabel="Approve without fee"
        confirmColor="var(--color-warning)"
        pending={approveMutation.isPending}
        onConfirm={onSkipFee}
      />
      <ReasonActionModal
        open={escalateOpen}
        onClose={(): void => setEscalateOpen(false)}
        title="Escalate for management review"
        warning="This ticket will be flagged for Tenant Admin review."
        placeholder="Explain why this ticket needs management review…"
        confirmLabel="Escalate"
        confirmColor="var(--color-danger)"
        pending={escalateMutation.isPending}
        onConfirm={onEscalate}
      />
      {changeSiteOpen && data !== undefined ? (
        <ChangeSiteModal
          target={{
            jobId,
            haulId: data.haulId,
            currentLabel:
              [data.site.siteName, data.site.sourceWellName ?? null]
                .filter((p) => p != null)
                .join(' · ') || null,
            hasCapture: data.capture.tanks.length > 0,
          }}
          open
          onClose={(): void => setChangeSiteOpen(false)}
          onChanged={(): void => setChangeSiteOpen(false)}
        />
      ) : null}
      {/* Mounted only while open, so an idle ticket view creates no void mutation. */}
      {voidOpen ? (
        <VoidLoadModal
          target={{ jobId, haulId: data?.haulId ?? null }}
          open
          onClose={(): void => setVoidOpen(false)}
          onVoided={(): void => {
            setVoidOpen(false);
            onBack();
          }}
        />
      ) : null}

      {/* DEC-190 override. No reason field: the approve body is `.strict()` and
          accepts only `approveNonBillable`, and the audit payload already records
          who overrode, when, and the unbillable reason. */}
      <Dialog
        open={overrideOpen}
        onOpenChange={(next): void => {
          if (!next) setOverrideOpen(false);
        }}
      >
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle>Approve as non-billable</DialogTitle>
          </DialogHeader>
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              borderRadius: '12px',
              border: '1px solid var(--color-warning)',
              backgroundColor: 'rgba(246, 182, 94, 0.12)',
              color: 'var(--color-text-primary)',
              fontSize: '13px',
              lineHeight: 1.45,
            }}
          >
            This ticket has real work on it ({bbl(t.billing.netBbl)} bbl
            {t.billing.hours > 0 ? `, ${formatNumber(t.billing.hours, { decimals: 2 })} hr` : ''})
            but no rate, so approving it now will produce{' '}
            <strong>no billable events and no invoice</strong>. The work cannot be billed later
            without re-opening the ticket. Your name and this override are recorded in the audit
            trail.
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={(): void => setOverrideOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={approveMutation.isPending}
              data-testid="confirm-non-billable"
              onClick={(): void => onApprove(true)}
              style={{
                backgroundColor: 'var(--color-warning)',
                color: 'var(--color-on-status)',
                borderColor: 'var(--color-warning)',
              }}
            >
              {approveMutation.isPending ? 'Approving…' : 'Approve without billing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GpsTrailModal
        apiOpts={apiOpts}
        jobId={showTrail ? jobId : null}
        haulId={t.haulId}
        open={showTrail}
        onClose={(): void => {
          setShowTrail(false);
        }}
      />

      {lightbox !== null && (
        <div
          onClick={(): void => {
            setLightbox(null);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            backgroundColor: 'rgba(0,0,0, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px',
            cursor: 'zoom-out',
          }}
        >
          <img
            src={lightbox}
            alt="Full size"
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
          />
        </div>
      )}
    </div>
  );
}

function footerActionStyle(color: string): React.CSSProperties {
  return {
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: 600,
    // The footer stretches its actions to the help text's height; a full pill at that height
    // reads as a circle, so these keep a rounded rectangle.
    borderRadius: '14px',
    border: 'none',
    backgroundColor: color,
    color: 'var(--color-on-status)',
    cursor: 'pointer',
  };
}

function fmt(iso: string | null, tz: string): string {
  return iso === null ? '—' : formatCreated(iso, tz);
}

function PanelShell({
  onBack,
  children,
}: {
  onBack: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--color-border-primary)',
          backgroundColor: 'var(--color-bg-secondary)',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            border: 'none',
            background: 'none',
            color: 'var(--color-accent-blue)',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          <ArrowLeft size={15} aria-hidden="true" /> Back to queue
        </button>
      </div>
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-tertiary)',
          fontSize: '14px',
        }}
      >
        {children}
      </div>
    </div>
  );
}
